import React, { useState, useEffect } from 'react';
import { Phone, Clock, Shield, CheckCircle, MapPin, Zap } from 'lucide-react';
import { companyInfo, images } from '../data/mock';
import { Button } from './ui/button';

const Hero = () => {
  const [bgLoaded, setBgLoaded] = useState(false);

  // Preload background image
  useEffect(() => {
    const img = new Image();
    img.src = images.cannes;
    img.onload = () => setBgLoaded(true);
  }, []);

  return (
    <section className="relative min-h-screen pt-36 sm:pt-40 md:pt-44 pb-12 md:pb-20 overflow-hidden">
      {/* Background with overlay - optimized loading */}
      <div className="absolute inset-0">
        {/* Gradient placeholder while image loads */}
        <div className="absolute inset-0 bg-gradient-to-br from-sky-900 via-sky-800 to-teal-700" />
        
        {/* Background image with fade-in */}
        <div 
          className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-500 ${bgLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ backgroundImage: `url(${images.cannes})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-sky-900/90 via-sky-800/85 to-teal-700/80" />
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-lime-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-sky-400/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 md:px-4 md:py-2 rounded-full mb-4 md:mb-6 border border-white/20">
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-lime-400"></span>
              </span>
              <span className="text-sm font-medium">Intervention {companyInfo.availability}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="text-white">Votre expert</span>
              <br />
              <span className="text-white">débouchage canalisation</span>
              <br />
              <span className="text-white">et</span> <span className="text-lime-400">assainissement</span>
              <br />
              <span className="text-sky-200">Alpes-Côte d'Azur</span>
            </h1>

            <p className="text-base md:text-xl text-sky-100 mb-8 leading-relaxed max-w-xl">
              Experts en débouchage de canalisations, entretien de bac à graisse, 
              réparation et dépannage pompe de relevage sur <strong>Cannes et les Alpes-Maritimes</strong>. 
              Intervention rapide, devis gratuit.
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3 md:gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-3 rounded-lg min-h-[48px]">
                <Clock className="text-lime-400" size={20} />
                <span className="text-base font-medium">Urgence 24h/24</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-3 rounded-lg min-h-[48px]">
                <Shield className="text-lime-400" size={20} />
                <span className="text-base font-medium">Devis Gratuit</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-3 rounded-lg min-h-[48px]">
                <MapPin className="text-lime-400" size={20} />
                <span className="text-base font-medium">Tout le 06</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}>
                <Button size="lg" className="bg-lime-500 hover:bg-lime-400 text-gray-900 font-bold px-6 md:px-8 py-4 md:py-6 text-base md:text-lg rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 min-h-[52px]">
                  <Phone size={22} className="mr-2" />
                  Appeler maintenant
                </Button>
              </a>
              <a href="#tarifs">
                <Button size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-sky-800 font-bold px-6 md:px-8 py-4 md:py-6 text-base md:text-lg rounded-full transition-all duration-300 min-h-[52px]">
                  Voir nos tarifs
                </Button>
              </a>
            </div>
          </div>

          {/* Right Content - Floating Card */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Main Card */}
              <div className="bg-white rounded-3xl shadow-2xl p-8 transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center">
                    <Zap className="text-sky-600" size={32} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Intervention Rapide</h3>
                    <p className="text-gray-500">En moins de 2 heures</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {[
                    "Débouchage canalisations",
                    "Curage haute pression 300 bars",
                    "Inspection caméra",
                    "Entretien bac à graisse",
                    "Contrat pompe de relevage"
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="text-lime-500 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">À partir de</p>
                      <p className="text-3xl font-bold text-sky-600">179€ <span className="text-base font-normal text-gray-400">HT</span></p>
                    </div>
                    <a href="#contact">
                      <Button className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-3 rounded-xl font-semibold">
                        Devis gratuit
                      </Button>
                    </a>
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-lime-500 text-gray-900 px-6 py-3 rounded-2xl shadow-xl font-bold">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">⭐</span>
                  <div>
                    <p className="text-lg font-bold">4.9/5</p>
                    <p className="text-xs">+100 avis</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;